<script setup>
import { computed, ref } from "vue";
import { useRouter } from "vue-router";
import {
  StealieButton,
  StealieHeading,
  StealiePhoneInput,
  StealieTextInput,
} from "@get-overalls/fe-stealie";
import WelcomeLayout from "@/layouts/WelcomeLayout.vue";
import { sendAnswers } from "@/services/TemporaryAnswerService";

const router = useRouter();
const phone = ref();
const address = ref({});
const errors = ref({});

const hasSomeAddressFilled = computed(() => {
  return Object.values(address.value).some((value) => value);
});

const handleSubmit = async () => {
  errors.value = {};
  const answers = [{ answer_id: "104", value: phone.value }];

  if (hasSomeAddressFilled.value) {
    answers.push({
      answer_id: "611",
      value: [
        { answer_id: "148", value: address.value.address1 },
        { answer_id: "149", value: address.value.address2 },
        { answer_id: "150", value: address.value.city },
        { answer_id: "151", value: address.value.state },
        { answer_id: "311", value: address.value.postalCode },
        { answer_id: "313", value: address.value.country },
      ],
    });
  }
  try {
    await sendAnswers(answers);
    router.push({ name: "Dashboard" });
  } catch (error) {
    console.error("Error submitting answers:", error);
  }
};
</script>

<template>
  <WelcomeLayout progress-step="3">
    <div
      class="mx-auto flex w-fit flex-col items-center gap-y-8 sm:max-w-5xl sm:px-4"
      data-sr-unmask
    >
      <StealieHeading
        level="1"
        size="lg"
        class="!text-3xl sm:self-start sm:!text-h1 sm:text-balance"
      >
        Let's keep in touch
      </StealieHeading>
      <StealieParagraph class="self-start sm:max-w-5xl">
        Please share your phone number and primary address. We'll keep your number on file
        in case you'd like to text with your LifeConcierge™. And we'll use your address
        to help find services in your area.
      </StealieParagraph>

      <form id="contact-info-form" class="w-full" @submit.prevent="handleSubmit">
        <div class="mb-12 max-w-56">
          <StealiePhoneInput
            v-model="phone"
            name="phone-number"
            label="Phone number"
            mask="(999) 999-9999"
            slot-char=" "
            placeholder="E.g., (212) 555-1234"
          />
        </div>
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
          <div class="col-span-full">
            <StealieTextInput
              v-model="address.address1"
              name="address1"
              label="Address line 1"
              autocomplete="address-line1"
              :error-message="errors.address1"
              :required="hasSomeAddressFilled"
            />
          </div>
          <div class="col-span-full">
            <StealieTextInput
              v-model="address.address2"
              name="address2"
              label="Address line 2"
              autocomplete="address-line2"
              :error-message="errors.address2"
            />
          </div>
          <div>
            <StealieTextInput
              v-model="address.city"
              name="city"
              label="City"
              autocomplete="address-level2"
              :error-message="errors.city"
              :required="hasSomeAddressFilled"
            />
          </div>
          <div>
            <StealieTextInput
              v-model="address.state"
              name="state"
              label="State / Province"
              autocomplete="address-level1"
              :error-message="errors.state"
              :required="hasSomeAddressFilled"
            />
          </div>
          <div>
            <StealieTextInput
              v-model="address.postalCode"
              name="postal-code"
              label="Postal code"
              autocomplete="postal-code"
              :error-message="errors.postalCode"
              :required="hasSomeAddressFilled"
            />
          </div>
          <div>
            <StealieTextInput
              v-model="address.country"
              name="country"
              label="Country"
              autocomplete="country"
              :error-message="errors.country"
              :required="hasSomeAddressFilled"
            />
          </div>
        </div>
      </form>
      <div class="flex gap-x-4">
        <StealieButton variant="secondary" :to="{ name: 'AboutYou' }">Back</StealieButton>
        <StealieButton form="contact-info-form" type="submit">Done</StealieButton>
      </div>
      <StealieParagraph size="sm">
        We will not share this with third parties without your consent.
      </StealieParagraph>
    </div>
  </WelcomeLayout>
</template>
